import { DownloadOutlined, PrinterOutlined } from '@ant-design/icons';
import { Button, FormInstance, Table } from 'antd';
import ColumnVisibilityDropdown from 'components/UI/Responsive/ColumnVisibilityDropdown';
import { OfferTypeEnum } from 'enums/offerType';
import { useColumnVisibility } from 'hooks/table/useColumnVisibility';
import { Order } from 'model/Order';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MY_ORDERS_COLUMN_GROUPS,
  MY_ORDERS_COLUMN_KEYS,
  buildMyOrdersColumns,
  groupMyOrdersColumns,
} from './myOrdersTableColumns';
import * as S from './styles';
import { useMyOrdersReport } from './useMyOrdersReport';

interface Props {
  offerType: OfferTypeEnum;
  isLoading?: boolean;
  data: Order[] | undefined;
  totalCount: number;
  form: FormInstance;
  onRowClick?: (order: Order) => void;
}

export default function MyOrdersTable({
  data,
  offerType,
  isLoading = false,
  totalCount,
  form,
  onRowClick,
}: Props) {
  const { t: translate } = useTranslation();
  const { downloadExcel, print } = useMyOrdersReport(form, data);
  const { visibleKeys, toggleColumn, resetColumns } = useColumnVisibility(
    'myOrders',
    MY_ORDERS_COLUMN_KEYS,
  );
  const columns = useMemo(
    () => groupMyOrdersColumns(buildMyOrdersColumns(translate), visibleKeys),
    [translate, visibleKeys],
  );

  return (
    <>
      <S.Toolbar>
        <S.Count>{totalCount.toLocaleString('pt-BR')} itens</S.Count>
        <S.ToolbarActions>
          <ColumnVisibilityDropdown
            groups={MY_ORDERS_COLUMN_GROUPS}
            visibleKeys={visibleKeys}
            onToggle={toggleColumn}
            onReset={resetColumns}
          />
          <Button
            aria-label="Exportar Excel"
            onClick={() => downloadExcel()}
            icon={<DownloadOutlined />}
          />
          <Button
            aria-label="Imprimir"
            onClick={() => print()}
            icon={<PrinterOutlined />}
          />
        </S.ToolbarActions>
      </S.Toolbar>
      <S.GroupedTable>
        <Table
          loading={isLoading}
          dataSource={data}
          pagination={false}
          columns={columns}
          scroll={{ x: 'max-content' }}
          bordered
          onRow={(order: Order) => ({
            onClick: () => onRowClick?.(order),
          })}
        />
      </S.GroupedTable>
    </>
  );
}
